'use client';

import { useState, useEffect, useRef, useMemo, useId } from 'react';
import { isFilled, asText } from "@prismicio/client";
import { motion, useReducedMotion } from 'framer-motion';

const TextMarquee = ({ slice }) => {
  const uuid = useId();
  const pathId = `wavy-path-${uuid}`;
  const shouldReduceMotion = useReducedMotion();
  const measureRef = useRef(null);

  // --- 1. ETAT ---
  const [textWidth, setTextWidth] = useState(0);
  const [deviceType, setDeviceType] = useState('desktop');

  // --- 2. CONFIGURATION ---
  const CONFIG = {
    mobile: {
      speed: 25,
      waveHeight: 25,
      targetWaveWidth: 250,
      ribbonThickness: 45,
      ribbonColor: "#FDF6D2"
    },
    tablet: {
      speed: 35,
      waveHeight: 40,
      targetWaveWidth: 400,
      ribbonThickness: 60,
      ribbonColor: "#FDF6D2"
    },
    desktop: {
      speed: 40,
      waveHeight: 50,
      targetWaveWidth: 600,
      ribbonThickness: 70,
      ribbonColor: "#FDF6D2"
    }
  };

  const { speed, waveHeight, targetWaveWidth, ribbonThickness, ribbonColor } = CONFIG[deviceType];

  // --- 3. DEFINITION DES CLASSES CSS ---
  const textClasses = "font-marquee font-bold uppercase text-moyam-orange fill-current";

  // --- 4. PRÉPARATION DU TEXTE ---
  const separator = "\u00A0\u00A0•\u00A0\u00A0";
  let baseText = "";
  let textParts = [];
  if (isFilled.group(slice.primary.texts)) {
    const texts = slice.primary.texts
      .filter(item => isFilled.keyText(item.text) || isFilled.richText(item.text))
      .map(item => isFilled.keyText(item.text) ? item.text : asText(item.text));

    baseText = texts.join(separator) + separator;

    texts.forEach((text, i) => {
      textParts.push({ text, isSeparator: false });
      textParts.push({ text: separator, isSeparator: true });
    });
  } else {
    return null;
  }

  // --- 5. DETECTION ECRAN & MESURE ---
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      if (width < 768) {
        setDeviceType('mobile');
      } else if (width >= 768 && width < 1024) {
        setDeviceType('tablet');
      } else {
        setDeviceType('desktop');
      }

      if (measureRef.current) {
        setTextWidth(measureRef.current.offsetWidth);
      }
    };
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [baseText]);

  // --- 6. GÉOMÉTRIE CALIBRÉE ---
  const geometry = useMemo(() => {
    if (!textWidth) return null;

    const wavesCount = Math.max(1, Math.round(textWidth / targetWaveWidth));
    const actualWaveWidth = textWidth / wavesCount;

    let screenWidthEstimate = 4000;
    if (deviceType === 'mobile') screenWidthEstimate = 1000;
    if (deviceType === 'tablet') screenWidthEstimate = 2000;

    const repetitions = Math.ceil(screenWidthEstimate / textWidth) + 2;

    const totalWaves = wavesCount * repetitions;
    const verticalShift = waveHeight + (ribbonThickness / 2);

    let d = `M 0,${verticalShift}`;
    for (let i = 0; i < totalWaves; i++) {
      d += ` q ${actualWaveWidth / 2} ${-waveHeight}, ${actualWaveWidth} 0 t ${actualWaveWidth} 0`;
    }
    const totalHeight = (waveHeight * 2) + ribbonThickness;

    return {
      pathData: d,
      totalWidth: actualWaveWidth * totalWaves,
      repetitions,
      totalHeight
    };
  }, [textWidth, waveHeight, targetWaveWidth, ribbonThickness, deviceType]);


  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="w-full relative z-10 -translate-y-13 md:-translate-y-18 lg:-translate-y-21 h-0 z-210"
    >
      {/* BLOC DE MESURE */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className={`absolute opacity-0 pointer-events-none whitespace-nowrap w-screen overflow-hidden ${textClasses}`}
      >
        {baseText}
      </div>
      <div className='w-full overflow-hidden'>
        {/* SVG ANIMÉ */}
        {textWidth > 0 && geometry && (
          <svg
            className="w-full block"
            viewBox={`0 0 ${geometry.totalWidth} ${geometry.totalHeight}`}
            xmlns="http://www.w3.org/2000/svg"
            style={{
              width: `${geometry.totalWidth}px`,
              maxWidth: 'none',
              willChange: 'transform',
              transform: 'translateZ(0)'
            }}
          >
            <defs>
              <path id={pathId} d={geometry.pathData} />
            </defs>
            <path
              d={geometry.pathData}
              fill="none"
              stroke={ribbonColor}
              strokeWidth={ribbonThickness}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="opacity-100"
            />

            <text
              className={textClasses}
              style={{
                fontFamily: "roc-grotesk-compressed, sans-serif",
                fontWeight: 700,
                fontSize: deviceType === 'mobile' ? '25px' : '45px',
                transform: deviceType === 'mobile' ? 'translateY(7px)' : 'translateY(15px)'
              }}
            >
              <motion.textPath
                href={`#${pathId}`}
                spacing="auto"
                initial={{ startOffset: 0 }}
                animate={{
                  startOffset: shouldReduceMotion ? 0 : -textWidth
                }}
                transition={{
                  duration: speed,
                  ease: "linear",
                  repeat: Infinity,
                }}
              >
                {Array(geometry.repetitions).fill(null).map((_, repIndex) => (
                  textParts.map((part, partIndex) => (
                    <tspan
                    className={part.isSeparator ? 'text-xl md:text-4xl' : ''}
                      key={`${repIndex}-${partIndex}`}
                      style={{
                        fontFamily: part.isSeparator ? 'Arial, sans-serif' : 'inherit',
                      }}
                    >
                      {part.text}
                    </tspan>
                  ))
                ))}
              </motion.textPath>
            </text>
          </svg>
        )}
      </div>
    </section>
  );
};

export default TextMarquee;